// Page-level helpers shared by both maps: the callsign, leaderboards and the end-of-round dialog.

import { isMapId, type MapId } from "../shared/maps";
import { normalizeName } from "../shared/score";
import { fetchLeaderboard, type LeaderboardEntry, type SubmitResult } from "./api";

const NAME_KEY = "catalyst-game.callsign";

export const $ = <T extends HTMLElement = HTMLElement>(sel: string) => document.querySelector<T>(sel)!;

let callsign: string | null = (() => {
  try {
    return normalizeName(localStorage.getItem(NAME_KEY));
  } catch {
    return null;
  }
})();

export function getCallsign(): string | null {
  return callsign;
}

export function setCallsign(name: string): void {
  callsign = name;
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Not remembered across visits; fine.
  }
}

/** Fills every `.leaderboard[data-map]` list on the page. */
export async function refreshLeaderboards(): Promise<void> {
  const lists = [...document.querySelectorAll<HTMLOListElement>(".leaderboard[data-map]")];
  const maps = [...new Set(lists.map((l) => l.dataset.map).filter(isMapId))];
  const results = new Map<MapId, LeaderboardEntry[] | null>();
  await Promise.all(
    maps.map(async (m) => {
      try {
        results.set(m, await fetchLeaderboard(m));
      } catch {
        results.set(m, null);
      }
    }),
  );
  const me = callsign?.toLowerCase();
  for (const list of lists) {
    const entries = results.get(list.dataset.map as MapId);
    const limit = Number(list.dataset.limit ?? 20);
    list.replaceChildren();
    if (!entries) {
      list.innerHTML = `<li class="empty">Leaderboard unavailable</li>`;
      continue;
    }
    if (entries.length === 0) {
      list.innerHTML = `<li class="empty">No scores yet. Be the first.</li>`;
      continue;
    }
    for (const e of entries.slice(0, limit)) {
      const li = document.createElement("li");
      if (e.name.toLowerCase() === me) li.className = "me";
      const name = document.createElement("span");
      name.textContent = e.name; // textContent: names are user input
      const score = document.createElement("b");
      score.textContent = String(e.score);
      li.append(name, score);
      list.append(li);
    }
  }
}

export interface RoundEnd {
  title: string;
  score: number;
  rows: [string, number][];
  /** Null when the round was never registered with the server. */
  submit: (() => Promise<SubmitResult>) | null;
  onClose: () => void;
}

const endDialog = () => $<HTMLDialogElement>("#end");
let closeHandler: (() => void) | null = null;

export function initEndDialog(): void {
  $("#play-again").addEventListener("click", () => endDialog().close());
  endDialog().addEventListener("close", () => {
    const h = closeHandler;
    closeHandler = null;
    h?.();
  });
}

export async function showRoundEnd(end: RoundEnd): Promise<void> {
  $("#end-title").textContent = end.title;
  $("#end-score").textContent = String(end.score);
  $("#end-breakdown").innerHTML = end.rows.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join("");
  const result = $("#end-result");
  result.textContent = end.submit ? "Submitting score…" : "This round was not registered, so it isn't on the leaderboard.";
  closeHandler = end.onClose;
  endDialog().showModal();

  if (end.submit) {
    try {
      const r = await end.submit();
      result.textContent = r.personalBest
        ? `New personal best! You're #${r.rank} on the leaderboard.`
        : `Your best is ${r.best} (#${r.rank}).`;
    } catch (err) {
      result.textContent = `Score not saved: ${(err as Error).message}`;
    }
  }
  void refreshLeaderboards();
}
